(ns hai.tasks.page
  (:require
   [garden.stylesheet]
   [hai.entity.data :as entity-data]
   [hai.learn-more :as learn-more]
   [hai.sharing.helper :as sharing]
   [hai.tasks.add-pill :as add-pill]
   [hai.tasks.assignee-bubble :as bubble]
   [hai.tasks.assignee-picker :as assignee-picker]
   [hai.tasks.card :as task-card]
   [hai.tasks.data :as data]
   [hai.tasks.eql :as tasks-eql]
   [hai.tasks.facets :as facets]
   [hai.tasks.modal :as task-modal]
   [hai.tasks.ui :as tasks-ui]
   [hai.tasks.urgency :as urgency]
   [hai.ui.components :as c]
   [hai.ui.components.card :as card]
   [hai.ui.components.collection.search :as collection-search]
   [hai.ui.components.collection.table :as collection]
   [hai.ui.components.collection.views.kanban :as kanban]
   [hai.ui.components.form.core :as uiform]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e >e! defevent defevent-fx defsub]]
   [hai.ui.tokens :as t]
   [hai.web-app.add-menu :as add-menu]
   [hai.web-app.layout :as layout]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]
   [url.how-it-works :as how-it-works]))

(def ^:private person-icon (icons/mui-icon :outlined :person))
(def ^:private drag-handle-icon (icons/mui-icon :outlined :drag_indicator))

(defsub ::selected [db]
  (get db ::selected #{}))

(defsub ::selected? [db task-uuid]
  (contains? (get db ::selected #{}) task-uuid))

(defevent ::toggle-selected [db task-uuid]
  (let [selected (get db ::selected #{})]
    (assoc db ::selected (if (contains? selected task-uuid)
                           (disj selected task-uuid)
                           (conj selected task-uuid)))))

(defevent ::clear-selected [db]
  (dissoc db ::selected))

(defsub ::selected-tasks
  [selected <- [::selected]
   tasks    <- [::data/list]]
  (filterv #(contains? selected (:task/uuid %)) tasks))

;; Bare mutation forms (not `{(mutation …) [out]}` joins): EQL collapses
;; several joins that share a dispatch key, so only one would run. Bare calls
;; each execute. Mirrors the shared collection bulk-action.
(defevent-fx ::bulk-complete [{:keys [db]} task-uuids]
  ;; The completed-archive collection is only mounted (and registered under
  ;; `:collections`) while its section is expanded — resolving it while
  ;; collapsed would throw "No table found".
  {:db (dissoc db ::selected)
   :resolve {:query (mapv (fn [task-uuid]
                            (list 'task/complete {:task/uuid task-uuid}))
                          task-uuids)
             :on-success-fx (cond-> [[:dispatch [::data/load-all-my-tasks]]
                                     [:dispatch [:sync/changed {:scope :tasks}]]]
                              (::completed-open? db)
                              (conj [:dispatch [:table/resolve ::completed]]))}})

(defevent-fx ::bulk-delete [{:keys [db]} task-uuids]
  {:db (dissoc db ::selected)
   :resolve {:query (mapv (fn [task-uuid]
                            (list 'task/delete {:task/uuid task-uuid}))
                          task-uuids)
             :on-success-fx [[:dispatch [::data/load-all-my-tasks]]
                             [:dispatch [:sync/changed {:scope :tasks}]]]}})

;; Everything the page shows of the tasks, reloaded in place.
(defevent-fx ::refetch [_ _]
  {:fx [[:dispatch [::data/load-all-my-tasks]]
        [:dispatch [:table/resolve-if-mounted data/board-collection-id]]
        [:dispatch [:table/resolve-if-mounted ::completed]]
        [:dispatch [::data/load-facets]]]})

(o/defstyled task-avatar :span
  :inline-flex :items-center :justify-center :flex-shrink-0
  {:width          "26px"
   :height         "26px"
   :border-radius  "50%"
   :background     t/--card-cream
   :color          t/--card-forest
   :font-size      "11px"
   :font-weight    "700"
   :letter-spacing "0.02em"
   :user-select    "none"}
  [:svg {:width "16px" :height "16px"}]
  ;; Current user: hollow ring instead of a fill — a shape distinction that
  ;; survives grayscale and doesn't fight the row's sage hover tint.
  [:&.me
   {:background "transparent"
    :box-shadow "inset 0 0 0 1px var(--task-accent)"
    :color      t/--task-accent}])

(defn- assignee-avatar [assignee]
  (let [me?      (and assignee (data/assignee-me? @(<s :auth/identity) assignee))
        initials (data/assignee-initials assignee)]
    [task-avatar {:title (or (data/assignee-name assignee) "Unassigned")
                  :class (when me? "me")}
     (or initials  [person-icon])]))

;; ---------------------------------------------------------------------------
;; Now / Next / Later columns — one card per task, the mini-app's.
;; ---------------------------------------------------------------------------

;; The board's card: the mini-app's row, put together from `hai.tasks.card`'s
;; pieces, with, for whoever may move it, the drag handle at its tail. As in
;; the mini-app, the stamp is the open task's. The handle is a cue only — the
;; whole card drags — so it carries no listeners and shows on hover.
(o/defstyled board-card :div
  [:.drag-handle
   :flex :items-center :justify-center :flex-shrink-0
   {:width "18px"
    :margin-right "-6px"
    :opacity "0"
    :color t/--card-forest-70
    :cursor "grab"}
   [:svg {:width "18px" :height "18px"}]]
  [:at-media {:hover "hover"}
   [:&:hover [:.drag-handle {:opacity "1"}]]])

;; Card renderer for the kanban board. The collection card-render hook always
;; calls `(render collection row)` with a form-wrapped row, so we take that shape
;; directly and unwrap `:form/current-value` here - there is no separate adapter
;; shim ([HAI-001-05]; the unwrap is structural to the card hook, not removable).
(defn- task-row-view [_collection row]
  (let [task (:form/current-value row)
        task-uuid (:task/uuid task)
        can-edit? (sharing/can-edit? task)
        highlighted? @(<s [::data/highlighted? task-uuid])
        ;; The new :query board ([HAI-001-04]) renders from `:collections`, not
        ;; the `::data/tasks` slice these mutations optimistically patch. For
        ;; instant feedback ([HAI-001-07]) each per-task action optimistically
        ;; patches the board's rows *now* (`:table/patch-row`/`:table/remove-row`),
        ;; then this success re-resolve reconciles against server truth - the
        ;; authority that also self-heals a rejected mutation (`/resolve` answers
        ;; 200 on rejection, so `:on-success-fx` runs and re-fetches). The board
        ;; is always mounted here, so this re-resolve is unconditional (unlike
        ;; the archive's `::completed`, which is only registered while expanded,
        ;; and unlike create's cross-surface `:table/resolve-if-mounted`).
        resolve-board-fx [[:dispatch [:table/resolve data/board-collection-id]]]
        open! #(>e [::task-modal/open task])]
    [board-card
     [task-card/task-shell
      {:class (cond-> ["edged"]
                highlighted? (conj "just-added"))}
      [task-card/task-row (task-card/pressable open!)
       [:div.task-row-main
        [task-card/title-block task]
        [bubble/component {:task task :change-fx resolve-board-fx}]
        (when can-edit?
          [:span.drag-handle {:aria-hidden true} [drag-handle-icon]])]
       [task-card/row-foot {:task task
                            :on-open-entity entity-data/open-entity}]]]]))

(o/defstyled bulk-controls-bar :div
  :flex :items-center
  {:gap "12px"}
  [:.count {:font-size "14px" :font-weight "600" :color t/--card-forest}]
  [:.clear
   {:padding "6px 10px"
    :background "transparent" :border "none"
    :color t/--card-forest-70 :font-family "inherit"
    :font-size "13px" :cursor "pointer"}
   [:&:hover {:color t/--card-forest}]])

(defn- bulk-confirm-dialog [!confirm]
  (let [{:keys [action tasks]} @!confirm
        n       (count tasks)
        delete? (= action :delete)]
    [c/AlertDialogRoot
     {:open         (some? @!confirm)
      :onOpenChange (fn [open?] (when-not open? (reset! !confirm nil)))}
     [c/AlertDialogContent
      [c/AlertDialogTitle (if delete? "Delete tasks" "Complete tasks")]
      [c/AlertDialogDescription
       (if delete?
         (str "Delete " n " task" (when (not= 1 n) "s") "? This can't be undone.")
         (str "Mark " n " task" (when (not= 1 n) "s") " complete?"))]
      [c/Flex {:justify "end" :gap "3" :mt "4"}
       [c/Button {:variant  "soft" :color "gray"
                  :on-click #(reset! !confirm nil)}
        "Cancel"]
       [c/Button {:variant  "solid"
                  :color    (if delete? "red" "green")
                  :on-click (fn []
                              (>e [(if delete? ::bulk-delete ::bulk-complete)
                                   (mapv :task/uuid tasks)])
                              (reset! !confirm nil))}
        (if delete? "Delete" "Complete")]]]]))

(defn- bulk-controls []
  (r/with-let [!confirm (r/atom nil)]
    (let [selected-tasks @(<s [::selected-tasks])
          me             @(<s :auth/identity)
          total          (count selected-tasks)
          completable    (filterv #(data/can-complete? me %) selected-tasks)
          deletable      (filterv sharing/owner? selected-tasks)]
      [bulk-controls-bar
       (when (pos? total)
         [:<>
          [:span.count (str total " selected")]
          [c/DropdownMenuRoot
           [c/DropdownMenuTrigger
            [c/Button {:variant "soft" :color "gray"}
             "Bulk actions" [c/DropdownMenuTriggerIcon]]]
           [c/DropdownMenuContent
            (when (seq completable)
              [c/DropdownMenuItem
               {:onSelect (fn [_] (reset! !confirm {:action :complete :tasks completable}))}
               (str "Mark complete (" (count completable) ")")])
            (when (seq deletable)
              [c/DropdownMenuItem
               {:color    "red"
                :onSelect (fn [_] (reset! !confirm {:action :delete :tasks deletable}))}
               (str "Delete (" (count deletable) ")")])]]
          [:button.clear {:on-click #(>e [::clear-selected])} "Clear"]
          [bulk-confirm-dialog !confirm]])])))

;; ---------------------------------------------------------------------------
;; Completed archive — server-paginated + searchable via the collection
;; component, lazily mounted so nothing loads until the section is opened.
;; ---------------------------------------------------------------------------

(def ^:private chevron-icon (icons/mui-icon :outlined :chevron_right))

(defsub ::completed-open? [db]
  (get db ::completed-open? false))

(defevent ::toggle-completed [db]
  (update db ::completed-open? not))

(defevent-fx ::uncomplete-archived [_ task-uuid]
  {:resolve {:query [{(list 'task/uncomplete {:task/uuid task-uuid})
                      [:task/uuid]}]
             :on-success-fx [[:dispatch [:table/resolve ::completed]]
                             [:dispatch [::data/load-all-my-tasks]]
                             [:dispatch [:sync/changed {:scope :tasks}]]]}})

(defn- completed-when [completed-at]
  (when completed-at
    (.toLocaleDateString (js/Date. completed-at)
                         "en-US" #js {:month "short" :day "numeric"})))

(o/defstyled completed-title-link :span
  :cursor-pointer :block
  {:text-decoration "line-through"
   :color           t/--card-forest-70
   :font-size       "14px"
   :overflow-wrap   "anywhere"}
  [:&:hover {:color t/--card-forest}])

(o/defstyled completed-time-label :span
  {:white-space "nowrap" :font-size "13px" :color t/--card-forest-70})

(o/defstyled completed-row :div
  :flex :flex-row :items-center
  {:gap "12px" :padding "10px 14px"
   :background t/--colour-snow
   :border-radius "10px"
   :border-width "1px" :border-style "solid" :border-color t/--card-forest-10
   :transition "border-color 0.15s ease, background 0.15s ease"}
  [:&:hover {:border-color t/--card-sage :background t/--card-sage-soft-15}]
  [completed-title-link {:flex "1 1 auto" :min-width "0"}]
  [completed-time-label {:flex-shrink "0"}]
  [task-avatar {:flex-shrink "0"}])

(defn- completed-card [_collection item]
  (let [task     (:form/current-value item)
        allowed? (data/can-complete? @(<s :auth/identity) task)
        open     (fn [] (>e [::task-modal/open task]))]
    [completed-row
     [tasks-ui/stampbox {:done?      true
                         :task-title (:task/title task)
                         :disabled?  (not allowed?)
                         :on-toggle  #(>e [::uncomplete-archived (:task/uuid task)])}]
     [completed-title-link
      {:role        "button"
       :tab-index   0
       :on-click    (fn [_] (open))
       :on-key-down (fn [e]
                      (when (or (= "Enter" (.-key e)) (= " " (.-key e)))
                        (.preventDefault e)
                        (open)))}
      (:task/title task)]
     [completed-time-label (completed-when (:task/completed-at task))]
     [assignee-picker/component
      {:task-uuid (:task/uuid task)
       :editable? allowed?
       :trigger   [assignee-avatar (:task/assigned-to task)]}]]))

(o/defstyled completed-wrap :div
  [card/solution-grid {:grid-template-columns "1fr" :gap "8px"}])

(defn- completed-collection []
  [completed-wrap
   [collection/component
    {:id           ::completed
     :query        [{:user/completed-tasks
                     [{:collection/items
                       [:task/uuid :task/title :task/completed-at
                        :task/assigned-to :user.entity/permissions :user.entity/owner?]}]}]
     :paginate?    true
     :pagination   {:table/per-page 10 :direction :desc :sort-attrs [:task/completed-at]}
     :default-view :collection.view/cards
     :controls     (fn [table] [collection-search/component table])
     :card         completed-card
     :no-items     (fn [_] [c/Text {:color "gray"} [:em "No completed tasks yet."]])
     :columns      []}]])

(o/defstyled completed-toggle :button
  :flex :items-center :cursor-pointer
  {:gap         "8px"
   :margin-top  "36px"
   :background  "transparent"
   :border      "none"
   :padding     "0"
   :font-family "inherit"
   :font-size   "18px"
   :font-weight "700"
   :color       t/--card-forest}
  [:svg {:width "22px" :height "22px" :fill t/--card-forest-70
         :transition "transform 0.15s ease"}]
  [:&.open [:svg {:transform "rotate(90deg)"}]])

(defn- completed-section []
  (let [open? @(<s [::completed-open?])]
    [:section
     [completed-toggle {:class (when open? "open")
                        :on-click #(>e [::toggle-completed])}
      [chevron-icon] "Completed"]
     (when open?
       [:div {:style {:margin-top "16px"}}
        [completed-collection]])]))

;; ---------------------------------------------------------------------------
;; New tasks board ([HAI-001-04]) — the bespoke Now/Next/Later columns ported
;; onto the generic `:collection.view/kanban` view. It is a `:query` collection
;; so the collection's own (server-side) search drives it, formalising what
;; `::data/load-all-my-tasks` already did (a 500-row open-tasks fetch, newest
;; first). Rendered ALONGSIDE the bespoke board below for the HITL parity
;; review; the bespoke board, its search, and the `task-row-view` card adapter
;; are removed only after sign-off ([HAI-001-05]).
;; ---------------------------------------------------------------------------

(def ^:private board-lanes
  "The tasks board's columns, one per urgency, in the order the cards'
   sections (`data/bucket-tabs`) and the ＋ pill's menu list them. The first
   is the catch-all, so a task with no urgency at all lands there exactly as
   the cards do through `data/task-bucket`."
  [{:collection.view.kanban.lane/id :none
    :collection.view.kanban.lane/label (urgency/label urgency/none)
    :collection.view.kanban.lane/background t/--lane-surface
    :collection.view.kanban.lane/catch-all? true}
   {:collection.view.kanban.lane/id :now
    :collection.view.kanban.lane/label (urgency/label :task.urgency/now)
    :collection.view.kanban.lane/background t/--lane-surface}
   {:collection.view.kanban.lane/id :next
    :collection.view.kanban.lane/label (urgency/label :task.urgency/next)
    :collection.view.kanban.lane/background t/--lane-surface}
   {:collection.view.kanban.lane/id :later
    :collection.view.kanban.lane/label (urgency/label :task.urgency/later)
    :collection.view.kanban.lane/background t/--lane-surface}])

;; Filter values are set from the facet sidebar ([[hai.tasks.facets/panel]]) and
;; cleared from the header chips; no per-column filter widget ever renders. The
;; collection's Filter schema still requires a `:field`, hence this stub (mirrors
;; the notes wall's `sidebar-only-filter-field`).
(defn- sidebar-only-filter-field
  [_field-props]
  nil)

(defn- assignee-summary
  "Chip face for the assignee filter: the chosen assignee's name, or a no-value
   sentinel's label. Resolves the name by uuid from whichever options source is
   loaded - the OFF `::data/facets` or, under the unified flag, the board
   collection's `:collection.filters/options` (OFF does not load the collection
   options and ON does not load the facets)."
  [current-value _column]
  (if (keyword? current-value)
    "Unassigned"
    (or (some (fn [{uuid :user/uuid full-name :user/full-name}]
                (when (= uuid current-value) full-name))
              (:facet/assignees @(<s [::data/facets])))
        (some (fn [{:keys [value label]}]
                (when (= value current-value) label))
              (:task/assigned-to @(<s [:collection.filters/options data/board-collection-id])))
        "Anyone")))

(defn- tags-summary
  "Chip face for the tag filter: the chosen tag, `#`-prefixed so it reads as a
   tag (`#work`), or the `:untagged` sentinel's label. The value is a one-element
   name vector `[tag-name]` or the `:untagged` keyword (see the facet sidebar)."
  [current-value _column]
  (cond
    (keyword? current-value) "Untagged"
    (first current-value)    (str "#" (first current-value))
    :else                    "Any tag"))

(o/defstyled board-search :div
  "Brand theming for the generic collection search on the tasks board so it reads
   as a task-token twin of the notes wall's header search ([HAI-CHROME-02]).
   Consumer-side by design: the brand tokens (forest / sage / snow) stay here in
   web-app and the shared `collection/search` component keeps its neutral
   design-system look - this wrapper just restyles the radix `TextField`
   descendants that component renders. It neutralises the radix surface variant's
   own box-shadow outline and 2px focus ring, then applies a real 1px sage border
   in the task palette: a visible `--card-sage-light` resting edge that deepens to
   `--card-sage` on focus, mirroring the notes search's `note-sage-35`/`-55`
   two-step. Sizing (13px text, 340px basis, 7px/12px padding) matches the notes
   header search so the two read as the same control in two tints."
  ;; Standalone spacing to the lanes when the search is not hosted in the sticky
  ;; header (the header zeroes this); mirrors the collection wrapper's own gap.
  ;; This wrapper is the header's flex child (as the notes `input.header-search`
  ;; is its own), so the 340px basis lives here - on the inner radix root it would
  ;; collapse, the wrapper being an `auto`-basis block that content-sizes.
  {:margin-bottom "16px"
   :flex          "0 1 340px"
   :min-width     "160px"}
  [:.rt-TextFieldRoot
   {:width         "100%"
    ;; radix size-2 pins the root to 32px; let padding + the input's line-height
    ;; drive the ~36px height, matching the notes header search.
    :height        "auto"
    :padding       "7px 12px"
    :border-width  "1px"
    :border-style  "solid"
    :border-color  t/--card-sage-light
    :border-radius "8px"
    :background    t/--colour-snow
    :box-shadow    "none"
    ;; radix draws a 2px focus outline in its accent colour; suppress it and let
    ;; the sage border be the only focus affordance, as the notes input does.
    :outline       "none"
    :transition    "border-color 0.15s ease"}
   [:&:focus-within {:border-color t/--card-sage}]]
  [:.rt-TextFieldInput
   {;; radix ships the input a 0.5px bottom padding; zero it (the 7px/12px lives
    ;; on the root) so the box height stays on the notes search's ~36px.
    :padding     "0"
    :color       t/--card-forest
    :font-size   "13px"
    :font-family "inherit"}
   [:&::placeholder {:color t/--card-forest-70}]])

;; ---------------------------------------------------------------------------
;; Sticky page header — the notes wall's header shell ([[hai.notes.page]]),
;; retinted in the board's task tokens (the forest / sage / snow twins the facet
;; surfaces already wear). Hosts the board's EXISTING pieces — the collection
;; search (brand-styled `board-search` around `collection-search/component`) and
;; the `facets/applied-chips` — plus the actions cluster: the filter funnel,
;; the tasks card's ＋ pill, and the ⋮ view-settings popover.
;; ---------------------------------------------------------------------------

(o/defstyled task-page-header :header
  {:position "sticky" :top "0" :z-index "50"
   :display "flex" :align-items "center" :flex-wrap "wrap"
   :gap "10px"
   :padding "10px 14px"
   :background t/--tasks
   :border-bottom-width "2px" :border-bottom-style "dotted"
   :border-bottom-color t/--card-sage
   :border-radius "10px"}
  [:.header-icon
   {:display "inline-flex" :align-items "center"
    :color t/--card-forest}]
  [:.header-title
   {:font-size "16px" :font-weight "700" :line-height "1.2"
    :margin "0" :color t/--card-forest}]
  ;; The search's own bottom margin (its spacing to the lanes on the bespoke
  ;; board) is redundant in the header row, so zero it here.
  [board-search {:margin-bottom "0"}]
  ;; A display:contents slot keeps the chips inline without an extra box, so the
  ;; header children hold fixed positions and the settings form never remounts.
  [:.chips-slot {:display "contents"}]
  ;; The ＋ pill takes its ink from here; the buttons set their own.
  [:.header-actions
   {:display "flex" :align-items "center" :gap "4px" :margin-left "auto"
    :color t/--card-forest}]
  [:.header-btn
   {:width "28px" :height "28px"
    :border "none" :border-radius "999px"
    :background "transparent" :padding "0"
    :color t/--card-forest :cursor "pointer"
    :display "inline-flex" :align-items "center" :justify-content "center"
    :transition "background 0.15s ease"}
   [:&.active {:background t/--card-forest-10}]
   ;; Hover tint only where hover exists — on touch the browser pins :hover on
   ;; the last thing tapped, so a filter toggled off would stay tinted.
   [:at-media {:hover "hover"}
    [:&:hover {:background t/--card-forest-10}]]
   [:svg {:width "16px" :height "16px" :fill "currentColor"}]
   ;; Labeled variant (the filter button): icon + text, sized to content.
   ;; The bare ⋮ settings button keeps the round icon-only base.
   [:&.labeled
    {:width "auto" :gap "6px" :padding "0 12px"
     :font-family "inherit" :font-size "13px" :font-weight "600"}]])

;; The ⋮ popover's rows — the collection settings form's own look, narrowed to a
;; single setting (Case sensitive) over the Learn more link. No Admin view (low value here) and no view-mode
;; select: the board is kanban-only, so a view switcher would break that contract.
(o/defstyled settings-rows :div
  :flex :flex-col :gap-2
  [:& :> [:div :flex :flex-row :items-center :gap-2]])

(defn- header-controls
  "`:form/render` for the collection's settings sub-form, mounted in the header's
   actions cluster. The funnel toggles the sidebar via the `:show-filters-table?`
   setting — the single show-filters flag a single-view kanban board needs (the
   same flag the kanban view and the page layout gate on). The tasks card's ＋
   pill opens the shared add-task modal, which seeds at the urgency the pill
   names. The ⋮ popover exposes only `case-sensitive?`, which the board's
   server-side `:user.tasks/table` search honours, and the Learn more link."
  [{fields :form/fields}]
  (let [flag        (get fields :show-filters-table?)
        filters-on? (boolean (:form.field/current-value flag))
        toggle!     (fn [] ((:form.field/set-value! flag) (not filters-on?)))]
    [:<>
     [c/Tooltip {:content (if filters-on? "Hide filters" "Show filters")}
      [:button.header-btn
       {:type       "button"
        :class      ["labeled" (when filters-on? "active")]
        :aria-label (if filters-on? "Hide filters" "Show filters")
        :on-click   toggle!}
       [icons/filled-filter_alt]
       [:span "Filter"]]]
     [add-pill/component {:on-add #(>e [::add-menu/open-task-modal])}]
     [c/PopoverRoot
      [c/PopoverTrigger
       [:button.header-btn {:type "button" :aria-label "Tasks view settings"}
        [icons/filled-more_vert]]]
      [c/PopoverContent {:align "end"}
       [settings-rows
        [:label
         [:div
          [uiform/checkbox (get fields :case-sensitive?)]
          [c/Text {:title "search, filtering"} "Case sensitive"]]]
        [learn-more/popover-row {:url how-it-works/tasks-url}]]]]]))

(defn- page-chrome
  "`:controls` for the tasks board — the sticky header: task icon + title, the
   existing collection search, the applied-filter chips, and the actions cluster
   (mounted as the collection's `:table/settings` sub-form so the funnel and the
   case-sensitive toggle ride the same persistence + reload plumbing notes uses)."
  [table]
  (let [settings-field (get-in table [:table/form :form/fields :table/settings])]
    [task-page-header
     [:span.header-icon [icons/humble-task]]
     [:h1.header-title "Tasks"]
     [board-search [collection-search/component table]]
     [:span.chips-slot [facets/applied-chips table]]
     [:div.header-actions
      (when settings-field
        [uiform/component (assoc settings-field
                                 :form/render header-controls
                                 ;; A remount must never re-seed the sub-form —
                                 ;; the live funnel/case-sensitive state lives there.
                                 :form/suppress-setup-if-done? true)])]]))

;; ---------------------------------------------------------------------------
;; Board layout - the facet sidebar in a left column beside the lanes. Mirrors
;; the notes wall's `wall-layout`: a grid applied to the collection root, whose
;; flat children (the sticky header, the facet `sidebar`, the kanban `board`) are
;; placed into a 240px sidebar column + a flexible lanes column. The sidebar is
;; shown or hidden by the header's funnel toggle, via the `:show-filters-table?`
;; setting: base is a single lanes column (the kanban view renders no sidebar
;; sibling when the flag is off); `.with-sidebar` adds the 240px facet column
;; when it is on (default shown - the board declares `:show-filters-default? true`
;; since it is kanban-only and wants the facets visible from the start).
;; Collapses to a single stacked column on narrow screens.
;; ---------------------------------------------------------------------------

(o/defstyled board-layout :div
  [collection/component
   {:display               "grid"
    :grid-template-columns "minmax(0, 1fr)"
    :gap                   "16px"
    :align-items           "start"}]
  [:&.with-sidebar
   [collection/component
    {:grid-template-columns "240px minmax(0, 1fr)"}
    [task-page-header {:grid-column "1 / -1"}]
    [facets/sidebar {:grid-column "1" :grid-row "2"}]
    [kanban/board {:grid-column "2" :grid-row "2"}]]]
  [:at-media {:max-width "768px"}
   [:&.with-sidebar
    [collection/component
     {:grid-template-columns "minmax(0, 1fr)"}
     [#{task-page-header facets/sidebar kanban/board}
      {:grid-column "1" :grid-row "auto"}]]]])

;; ---------------------------------------------------------------------------
;; Loading skeleton — a resting-task-shaped placeholder shown once per lane
;; while the board's first resolve is in flight (the kanban view renders it for
;; a still-loading `nil` lane, in place of the `Nothing here.` empty state).
;; ---------------------------------------------------------------------------

#_{:clj-kondo/ignore [:clojure-lsp/unused-public-var]}
(o/defrules task-skeleton-pulse
  (garden.stylesheet/at-keyframes
   :hai-task-skeleton-pulse
   ["0%"   {:background-color c/--gray-3}]
   ["50%"  {:background-color c/--gray-5}]
   ["100%" {:background-color c/--gray-3}]))

;; A resting board card, box-for-box: `task-shell`'s edged surface around
;; `task-row`'s padding, with the title and assignee replaced by blocks whose
;; background pulses. The metrics are the real components' — the row's
;; `13px 14px 12px` padding and 14px gap, the assignee bubble 26px — so the
;; placeholder holds the box of a card with nothing under its title. Passed to
;; the kanban view as `:collection.view.kanban/skeleton`.
(o/defstyled task-skeleton :div
  {:background t/--task-card-surface-edged
   :border-radius "13px"
   :border-width "1px" :border-style "solid" :border-color t/--card-forest-10}
  [:.row
   :flex :flex-row :items-center
   {:gap "14px" :padding "13px 14px 12px"}]
  [:.pulse {:animation "hai-task-skeleton-pulse 1.4s ease-in-out infinite"}]
  [:.body
   :flex :flex-col :justify-center
   {:flex "1 1 auto" :min-width "0"}]
  [:.title {:height "14px" :max-width "70%" :border-radius "7px"}]
  [:.avatar {:width "26px" :height "26px" :border-radius "50%" :flex "0 0 auto"}]
  ([]
   [:<>
    [:div.row
     [:div.body [:div.pulse.title]]
     [:div.pulse.avatar]]]))

(defn- tasks-board
  "The tasks board as a generic `:query` kanban collection. Reproduces
   `::data/load-all-my-tasks`'s fetch — open tasks only, newest first, capped at
   500 — with the full `tasks-eql/task-eql` under `:collection/items` so avatars,
   permissions, deadlines and urgency bucketing all resolve. `data/task-bucket`
   buckets into the declared lanes; `task-row-view` is the card renderer - it
   takes the form-wrapped row straight from the card-render hook and unwraps it
   itself (no separate adapter)."
  []
  [collection/component
   {:id           data/board-collection-id
    ;; `:collection.filters/options` is served only under
    ;; :user/unified-relationships? ON (the composable-filter path). Requesting it
    ;; under OFF errors pathom - the legacy `execute` does not output the key - so
    ;; gate it on the flag; OFF reads its sidebar counts from :user/task-facets.
    :query        [{(list :user.tasks/table {:collection.filters/selected {:task/open? true}})
                    (cond-> [{:collection/items tasks-eql/task-eql}]
                      @(<s [:feature-flag/unified-relationships?])
                      (conj :collection.filters/options))}]
    :pagination   {:table/per-page 500 :sort-attrs [:task/created-at] :direction :desc}
    :default-view :collection.view/kanban
    :view-options [kanban/view-option]
    :collection.view.kanban/lanes     board-lanes
    :collection.view.kanban/bucket-fn data/task-bucket
    ;; Task-shaped loading placeholder shown per lane until the first resolve
    ;; lands (see [[task-skeleton]]).
    :collection.view.kanban/skeleton  [task-skeleton]
    ;; Drag-to-retriage ([HAI-003-04]): the generic spine calls this on a real
    ;; lane change (same-lane drops are already a no-op via `kanban/move-decision`).
    ;; Map the destination lane to a `:task/urgency` - None writes the *explicit*
    ;; `:task.urgency/none`, a value like the lanes, so clearing is the same
    ;; write as placing - then patch the board's row now for instant
    ;; re-bucketing, and run `::data/update` whose success re-resolve reconciles
    ;; against server truth (also the 001-07 Option-A self-heal - no new error
    ;; plumbing). `::data/update` is the production `task/update` transport, which
    ;; enforces edit permission server-side, so a non-editable card (already
    ;; non-draggable via the spine's `sharing/can-edit?` gate) is safe here too.
    :collection.view.kanban/on-card-move
    (fn [{:keys [row to-lane-id]}]
      (let [uuid    (:task/uuid row)
            urgency (case to-lane-id
                      :now   :task.urgency/now
                      :next  :task.urgency/next
                      :later :task.urgency/later
                      :none  :task.urgency/none)]
        ;; Synchronous (`>e!`) so the card is re-bucketed into the destination
        ;; lane *within* the drop's `onDragEnd`, before the source card un-dims
        ;; from its dragging placeholder. An async patch lands a tick later, so
        ;; the source card first snaps back to full opacity in its original lane
        ;; (a visible flash) and only then relocates. The server round-trip
        ;; (`::data/update`) stays async - only the local re-bucket is sync.
        (>e! [:table/patch-row data/board-collection-id :task/uuid uuid
              {:task/urgency urgency}])
        (>e [::data/update {:task-uuid uuid :urgency urgency}
             [[:dispatch [:table/resolve data/board-collection-id]]]])))
    :card         task-row-view
    ;; The sticky page header ([[page-chrome]]) - hosts the search, applied
    ;; chips, Add-task, the filter funnel and the ⋮ view-settings popover.
    :controls     page-chrome
    :search       {:placeholder "Search tasks…"}
    ;; Show the facet sidebar by default (the board is kanban-only, so the funnel
    ;; in the header is the only place to hide it - it should start visible).
    :show-filters-default? true
    ;; Assignee facet filter ([HAI-006-03/04]): the value is set from the facet
    ;; sidebar and shown in the header chips; no per-column widget renders.
    ;; `:sibling` mounts the sidebar as a sibling of the lane grid (a board has
    ;; no in-view header row to host it), and `board-layout` positions it in the
    ;; left column. Mirrors the notes wall.
    :filters/render    facets/panel
    :filters/placement :sibling
    :columns
    [;; Value is a one-element tag-name vector `[tag-name]` OR the `:untagged`
     ;; sentinel keyword. `:any` (not `[:or [:vector :string] :keyword]`) on
     ;; purpose, same reason as the assignee column: a compound schema trips the
     ;; form's default/field instantiation and drops the key. Fully
     ;; frontend-controlled (see the facet sidebar), so `:any` is safe. Declared
     ;; first so its applied chip leads, matching the notes wall + facet order.
     {:header ""
      :attr   :task/tags
      :sort?  false
      :render (fn [_] nil)
      :filter {:schema  :any
               :type    :filter/select
               :field   sidebar-only-filter-field
               :label   "Tag"
               :icon    icons/humble-tag
               :summary tags-summary}}
     {:header ""
      :attr   :task/assigned-to
      :sort?  false
      :render (fn [_] nil)
      ;; Value is a single `:user/uuid` OR the `:unassigned` sentinel keyword.
      ;; `:any` (not `[:or :uuid :keyword]`) on purpose, mirroring the notes
      ;; wall's frontend-controlled filters: a compound schema trips the form's
      ;; default/field instantiation and drops the key. The value is fully
      ;; frontend-controlled (see the facet sidebar), so `:any` is safe.
      :filter {:schema  :any
               :type    :filter/select
               :field   sidebar-only-filter-field
               :label   "Assignee"
               :icon    icons/outlined-person
               :summary assignee-summary}}]}])

(defn page [_route]
  ;; The sticky header now carries the title and Add-task control, so the generic
  ;; platform chrome (breadcrumbs / heading / lead*) is gone - parity with the
  ;; notes wall. `sidebar?` gates the layout's `.with-sidebar` grid on the
  ;; header funnel's `:show-filters-table?` setting (default shown), mirroring
  ;; `notes.page/page`.
  (let [settings @(<s [:form.field/current-value data/board-collection-id :table/settings])
        sidebar? (boolean (:show-filters-table? settings))]
    [layout/page
     [board-layout {:class (when sidebar? "with-sidebar")}
      [tasks-board]]
     ;; The bulk-selection subsystem is dormant/dead ([HAI-001-05] reports it;
     ;; deletion is deferred to another doc). Its only mount point was the bespoke
     ;; `controls-row`; keep it mounted here so it is neither deleted nor left an
     ;; unreferenced orphan - it renders nothing until `::selected` is populated
     ;; (which no UI does).
     [bulk-controls]
     [completed-section]
     ;; A card or a completed row pressed opens its task over the page; every
     ;; change there reloads what the page shows.
     [task-modal/task-modal {:change-fx [[:dispatch [::refetch]]]}]]))
